import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { MediaUpload } from '@/components/MediaUpload';
import { ArrowLeft, Loader2, Save, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = {
  slug: '',
  title_en: '',
  title_hi: '',
  excerpt_en: '',
  excerpt_hi: '',
  body_en: '',
  body_hi: '',
  cover_image: '',
  tags: [],
  author_name: 'Shree Bajrang Developers',
  is_published: true,
  meta_title_en: '',
  meta_title_hi: '',
  meta_description_en: '',
  meta_description_hi: '',
  canonical_url: '',
};

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function ArticleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEdit = Boolean(id);
  const [data, setData] = useState(blank);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data: list } = await axios.get(`${API}/articles/admin/all`, { headers });
        const found = list.find((a) => a.id === id);
        if (!found) throw new Error('Not found');
        setData({ ...blank, ...found });
      } catch {
        toast.error('Failed to load article');
        navigate('/dashboard/articles');
      } finally { setLoading(false); }
    })();
  }, [id]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addTag = () => {
    const v = tagInput.trim().replace(/^#/, '');
    if (!v) return;
    if (data.tags.includes(v)) { setTagInput(''); return; }
    update('tags', [...data.tags, v]);
    setTagInput('');
  };

  const removeTag = (tg) => update('tags', data.tags.filter((t) => t !== tg));

  // When typing English title, auto-fill slug if empty
  const onTitleEn = (v) => {
    setData((d) => ({
      ...d,
      title_en: v,
      slug: d.slug || slugify(v),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.title_en || !data.body_en) {
      toast.error('English title and body are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...data, slug: data.slug || slugify(data.title_en) };
      // Strip empty Hindi fields so backend AI auto-fills them
      ['title_hi', 'excerpt_hi', 'body_hi', 'meta_title_hi', 'meta_description_hi'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (isEdit) {
        await axios.put(`${API}/articles/${id}`, payload, { headers });
        toast.success('Article saved. Hindi version auto-translated by AI.');
      } else {
        await axios.post(`${API}/articles`, payload, { headers });
        toast.success('Article published. Hindi version auto-translated by AI.');
      }
      navigate('/dashboard/articles');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand-orange" /></div>;
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="article-form-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard/articles"><Button variant="outline" size="sm" type="button"><ArrowLeft className="h-4 w-4" /></Button></Link>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{isEdit ? 'Edit Article' : 'New Article'}</h1>
            <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-brand-orange" />
              Leave Hindi fields empty — AI will auto-translate using GPT-5.2 on save.
            </p>
          </div>
        </div>
        <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="save-article-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {isEdit ? 'Save Changes' : 'Publish'}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Content (English)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-1">
              <Label>Title *</Label>
              <Input value={data.title_en} onChange={(e) => onTitleEn(e.target.value)} required data-testid="article-title-en" />
            </div>
            <div className="space-y-1">
              <Label>URL Slug *</Label>
              <Input value={data.slug} onChange={(e) => update('slug', slugify(e.target.value))} required data-testid="article-slug" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Excerpt / Summary</Label>
            <Textarea rows={2} value={data.excerpt_en} onChange={(e) => update('excerpt_en', e.target.value)} data-testid="article-excerpt-en" />
          </div>
          <div className="space-y-1">
            <Label>Body (HTML supported) *</Label>
            <Textarea rows={14} value={data.body_en} onChange={(e) => update('body_en', e.target.value)} required className="font-mono text-sm" data-testid="article-body-en" />
            <p className="text-xs text-gray-500">Tip: paste HTML from your editor, e.g. &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;img src&gt;.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Cover & Tags</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label>Cover Image</Label>
            <MediaUpload type="image" value={data.cover_image} onChange={(v) => update('cover_image', v)} />
          </div>
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {data.tags.map((tg) => (
                <Badge key={tg} variant="outline" className="gap-1">
                  #{tg}
                  <button type="button" onClick={() => removeTag(tg)}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="e.g. plots, jamul, rera"
                data-testid="article-tag-input"
              />
              <Button type="button" variant="outline" onClick={addTag}>Add</Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Author Name</Label>
              <Input value={data.author_name || ''} onChange={(e) => update('author_name', e.target.value)} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label>Published</Label>
                <p className="text-xs text-gray-500">Toggle off to save as draft</p>
              </div>
              <Switch checked={data.is_published} onCheckedChange={(v) => update('is_published', v)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>SEO Metadata (English)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Meta Title</Label>
              <Input value={data.meta_title_en || ''} onChange={(e) => update('meta_title_en', e.target.value)} placeholder="Defaults to article title" />
            </div>
            <div className="space-y-1">
              <Label>Canonical URL</Label>
              <Input value={data.canonical_url || ''} onChange={(e) => update('canonical_url', e.target.value)} placeholder="https://..." />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Meta Description</Label>
            <Textarea rows={2} value={data.meta_description_en || ''} onChange={(e) => update('meta_description_en', e.target.value)} maxLength={160} />
            <p className="text-xs text-gray-500">{(data.meta_description_en || '').length}/160 characters</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-orange-200 bg-orange-50/30">
        <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand-orange" /> Hindi Version (Optional Override)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">If left blank, GPT-5.2 will auto-generate natural, real-estate-tone Hindi for you.</p>
          <div className="space-y-1">
            <Label>शीर्षक (Hindi Title)</Label>
            <Input value={data.title_hi || ''} onChange={(e) => update('title_hi', e.target.value)} dir="auto" />
          </div>
          <div className="space-y-1">
            <Label>संक्षेप (Hindi Excerpt)</Label>
            <Textarea rows={2} value={data.excerpt_hi || ''} onChange={(e) => update('excerpt_hi', e.target.value)} dir="auto" />
          </div>
          <div className="space-y-1">
            <Label>लेख (Hindi Body)</Label>
            <Textarea rows={10} value={data.body_hi || ''} onChange={(e) => update('body_hi', e.target.value)} dir="auto" className="font-mono text-sm" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Hindi Meta Title</Label>
              <Input value={data.meta_title_hi || ''} onChange={(e) => update('meta_title_hi', e.target.value)} dir="auto" />
            </div>
            <div className="space-y-1">
              <Label>Hindi Meta Description</Label>
              <Input value={data.meta_description_hi || ''} onChange={(e) => update('meta_description_hi', e.target.value)} dir="auto" />
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
